/**
 * AGENTVERSIONSERVICE - GERENCIAMENTO DE VERSÕES DE AGENTES
 *
 * Este serviço gerencia as VERSÕES dos agentes instalados.
 * Responsável por:
 * - Buscar versões de agentes por base do agente
 * - Atualizar status de versões (ativo, inativo, draft)
 * - Gerenciar ciclo de vida das versões
 * - Operações de rollback e restauração
 *
 * ENDPOINT: /agent-base/versions
 * ESTADO: Versões de agentes com diferentes status
 * PADRÃO: Facade para simplificar operações de versionamento
 *
 * RELACIONAMENTO: Integra com agentBaseService e agentTriggerService
 */

import { putApi, get, put, post } from "@/shared/utils/fetchApi";
import { AgentVersion } from "../types/AgentVersion";

/**
 * Serviço para gerenciar versões de agentes
 * Implementa o padrão Facade para encapsular as chamadas à API relacionadas a versões de agentes
 */
export class AgentVersionService {
  /**
   * Atualização em massa de status — só existe sob `/api`.
   *
   * A raiz tem `PUT /agent-versions/:id` (uma versão por vez) e não a variante em
   * massa que este método usa, por isso a chamada segue no helper compat. Os
   * demais métodos deste serviço já usam `/agent-versions` na raiz.
   */
  private static COMPAT_BASE_URL = "/agent-base/versions";

  /**
   * Obtém todas as versões de um agente pelo ID da base do agente
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_versions').select('*').eq('agent_base_id', agentId).order('created_at', { ascending: false })
   *
   * @param agentBaseId ID da base do agente
   * @returns Status da operação no formato { data, error }
   */
  static async getByAgentBaseId(
    agentBaseId: string,
  ): Promise<{ data: AgentVersion[] | null; error: Error | null }> {
    try {
      const res = await get<{ data: AgentVersion[] }>(
        `/agent-versions?agent_base_id=${agentBaseId}`,
      );
      return { data: res.data, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Atualiza o status de uma versão de agente
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_versions').update({ status }).eq('id', versionId)
   *
   * @param versionId ID da versão do agente
   * @param status Novo status para a versão
   * @returns Status da operação no formato { data, error }
   */
  static async updateStatus(
    versionId: string,
    status: string,
  ): Promise<{ data: AgentVersion | null; error: Error | null }> {
    try {
      const res = await put<AgentVersion>(`/agent-versions/${versionId}`, {
        status,
      });
      return { data: res, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Atualiza o status de todas as versões de um agente para um determinado status
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_versions').update({ status }).eq('agent_base_id', agentBaseId).eq('status', currentStatus)
   *
   * @param agentBaseId ID da base do agente
   * @param status Novo status para as versões
   * @param currentStatus Status atual das versões que serão atualizadas
   * @returns Status da operação no formato { data, error }
   */
  static async updateStatusByAgentBaseIdAndStatus(
    agentBaseId: string,
    status: string,
    currentStatus: string,
  ): Promise<{ data: AgentVersion | null; error: Error | null }> {
    try {
      const result = await putApi<{ data: AgentVersion }>(
        `${this.COMPAT_BASE_URL}`,
        {
          agent_base_id: agentBaseId,
          status,
          currentStatus,
        },
      );

      return { data: result.data, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Obtém o próximo número de versão para um agente
   *
   * @param agentBaseId ID da base do agente
   * @returns Próximo número de versão no formato vX.Y.Z
   */
  static async getNextVersionNumber(
    agentBaseId: string,
  ): Promise<{ data: { nextVersion: string } | null; error: Error | null }> {
    try {
      const res = await get<{ version: string }>(
        `/agent-bases/${agentBaseId}/next-version`,
      );
      return { data: { nextVersion: res.version }, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Publica uma nova versão de agente de forma simplificada
   * Arquiva automaticamente todas as versões antigas e cria uma nova versão atual
   *
   * @param versionData Dados da nova versão
   * @returns Versão criada
   */
  static async publishVersionSimplified(
    versionData: Record<string, unknown>,
  ): Promise<{ data: AgentVersion | null; error: Error | null }> {
    try {
      const res = await post<AgentVersion>(
        "/agent-versions/publish",
        versionData,
      );
      return { data: res, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Restaura uma versão específica como nova versão atual
   *
   * @param versionId ID da versão a ser restaurada
   * @returns Nova versão criada baseada na versão restaurada
   */
  static async restoreVersion(
    versionId: string,
  ): Promise<{ data: AgentVersion | null; error: Error | null }> {
    try {
      const res = await post<AgentVersion>(
        `/agent-versions/${versionId}/restore`,
        {},
      );
      return { data: res, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Obtém versões de agente com informações de webhook otimizadas
   *
   * @param agentBaseId ID da base do agente
   * @param page Página (opcional)
   * @param limit Limite por página (opcional)
   * @returns Versões com URLs de webhook otimizadas
   */
  static async getVersionsWithWebhooks(
    agentBaseId: string,
    page?: number,
    limit?: number,
  ): Promise<{ data: AgentVersion[] | null; error: Error | null }> {
    try {
      const params = new URLSearchParams({ agent_base_id: agentBaseId });
      if (page) params.append("page", page.toString());
      if (limit) params.append("limit", limit.toString());

      const res = await get<{ data: AgentVersion[] }>(
        `/agent-versions/with-webhooks?${params}`,
      );
      return { data: res.data, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }
}
